import React, { useContext, useState } from "react";
import { createPortal } from "react-dom";
import { Info } from "lucide-react";
import AppContext from "../../context/AppContext";
import AlertModal from "./AlertModal";
import Tooltip from "./Tooltip";
import CommonTooltip from "./CommonTooltip";
import translations from "./Translations";

// Badge shapes match the "generating" badge next to it in each list:
// "pill" = Export Report table, "chip" = Download modal's Recent Reports.
const shapeClasses = {
  pill: "rounded-full px-2.5 py-1 shadow-xs",
  chip: "rounded-lg px-3 py-1.5",
};

// "Failed" badge for a report row with an info icon. Error messages can be
// long/technical (DB or S3 errors, up to 1000 chars), so rather than printing
// them in the row, clicking the badge opens an alert with the full text and a
// single Ok button.
// `label` (e.g. "Performance Report") prefixes the badge where no column
// header already says which kind of report the row is.
// `useCommonTooltip` swaps the hover bubble for CommonTooltip, for screens that
// use it for every tooltip (e.g. the inventory export modal).
export default function ReportFailedStatus({
  errorMessage,
  label,
  variant = "pill",
  className = "",
  useCommonTooltip = false,
}) {
  const { theme, langMode } = useContext(AppContext);
  const [showError, setShowError] = useState(false);
  // With no errorMessage the title alone ("Report generation failed") says it all.
  const message = typeof errorMessage === "string" ? errorMessage.trim() : "";

  const badge = (
        <button
          type="button"
          onClick={() => setShowError(true)}
          aria-label={translations[langMode].reportGenerationFailed}
          aria-haspopup="dialog"
          data-testid="report-failed-badge"
          className={`inline-flex items-center gap-1.5 border text-xs font-medium whitespace-nowrap cursor-pointer transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-red-300 ${
            shapeClasses[variant] ?? shapeClasses.pill
          } ${
            theme === "dark"
              ? "border-red-500/30 bg-red-500/15 text-red-300 hover:bg-red-500/25"
              : theme === "high-contrast"
                ? "high-contrast border-2 font-bold"
                : "border-red-200/90 bg-red-50/80 text-red-700 hover:bg-red-100"
          }`}
        >
          <Info size={14} className="shrink-0 text-red-500 dark:text-red-400" />
          <span>
            {label ? `${label} – ` : ""}
            {translations[langMode].reportFailed}
          </span>
        </button>
  );

  return (
    <div className={`inline-flex items-center ${className}`}>
      {/* hidden while the alert is open so the bubble can't sit on top of it */}
      {useCommonTooltip ? (
        <CommonTooltip
          text={showError ? "" : translations[langMode].reportFailedTooltip}
          position="top"
          disableDefaultDescription
          tooltipClassName="w-max max-w-[min(15rem,calc(100vw-1.5rem))] [text-wrap:balance]"
          fitContent
          clampToViewport
          hoverOnly
        >
          {badge}
        </CommonTooltip>
      ) : (
        <Tooltip text={translations[langMode].reportFailedTooltip} position="top" disabled={showError}>
          {badge}
        </Tooltip>
      )}

      {/* Portaled to <body>: the Download modal's panel is transformed, which
          would otherwise trap this fixed overlay inside it. Headless UI treats
          body-level nodes as its own, so clicking Ok won't close that modal. */}
      {createPortal(
        <AlertModal
          open={showError}
          variant="error"
          title={translations[langMode].reportGenerationFailed}
          message={
            message && (
              <span className="block max-h-60 overflow-y-auto whitespace-pre-wrap break-words">
                {message}
              </span>
            )
          }
          showCloseIcon={false}
          onClose={() => setShowError(false)}
          dataTestId="report-error-alert"
        />,
        document.body
      )}
    </div>
  );
}
